/* eslint-disable indent */
import { Anchor, CascadingPanel, CascadingPanels, ChevronIcon, Chip, ListItemLayout, ListLayout, Toggle, useCascadingPanelsConnector, useMatchScreenWidth } from '@keplerco/core';
import React, { useEffect, useState } from 'react';
import { ListItem } from '../../../../components/lists/list-item';
import classNames from 'classnames';
import { EntitiesCardWidget } from '../../../../components/cards/entities-card.widget';
import { ImportSkillPanel } from '../../../analytics/assessments/import-skill.panel';
import { SelectSkillsLayout } from './select-skills.layout';
import { useAppActions } from '../../../../overmind';
import { StepSkeleton } from '../../../../components/general/stepper/step/step.skeleton';
import { CascadingFocusPanelIds, ErrorMessage, ISkillsStepLayoutProps } from './skills-step.models';
import styles from './skills-step.module.css';
import { CompanySkillListItemResponse } from '../../../../models/overmind/company-entity';
import layoutStyles from '../../../../design/layout.module.css';
import { IPerson } from '../../assessment-management.models';
import { PeerAssessmentAssigneeRequest } from '../../../../overmind/actions';

const CUSTOM_THEME = { backgroundColor: 'background', borderColor: 'baby-blue', color: 'text' };

export function SkillsStepLayout({ assessmentSlug, isLoading, setIsLoading, people, setPeople, roleSlug, onBack, onNext }: ISkillsStepLayoutProps): JSX.Element {
  const actions = useAppActions();
  const [selectedPeople, setSelectedPeople] = useState<IPerson[]>();
  const [selectedPerson, setSelectedPerson] = useState<IPerson>();
  const [applyToAll, setApplyToAll] = useState<boolean>(false);
  const [selectedSkills, setSelectedSkills] = useState<CompanySkillListItemResponse[]>([]);
  const [sync, setSync] = useState<string>(crypto.randomUUID());
  const [errorMessage, setErrorMessage] = useState<ErrorMessage>();

  const isMobile = useMatchScreenWidth('mobile');

  const { openPanelIds, next, previous } = useCascadingPanelsConnector();

  useEffect(() => {
    setSelectedPeople(people);
  }, [people]);

  useEffect(() => {
    if (!errorMessage) return;
    generateErrorMessage(selectedPeople);
  }, [selectedPeople, applyToAll, selectedSkills]);

  function generateErrorMessage(people: IPerson[] | undefined): ErrorMessage | undefined {
    let errorMessage = undefined;
    if (!people) return undefined;

    if (applyToAll) {
      errorMessage = !selectedSkills.length ? ErrorMessage.NoSkills : undefined;
      setErrorMessage(errorMessage);
      return errorMessage;
    }

    // In the 360-degree review model, all people must have skills assigned
    if (people.some(person => !person.skills.length)) {
      errorMessage = ErrorMessage.PeerReviewSkills;
    }

    setErrorMessage(errorMessage);
    return errorMessage;
  }

  function generateChips(person: IPerson): React.ReactElement[] {
    let chips: React.ReactElement[] = [
      <Chip key="manager" customTheme={CUSTOM_THEME}>
        Manager
      </Chip>,
      <Chip key="skills" customTheme={CUSTOM_THEME}>
        {applyToAll ? selectedSkills.length : person.skills.length} skill(s)
      </Chip>,
    ];

    // Only show the manager chip for people who are managers
    if (!person.manager) chips = chips.filter(chip => chip.key !== 'manager');

    return chips;
  }

  if (isLoading) return <StepSkeleton />;

  return (
    <React.Fragment>
      <EntitiesCardWidget title="Skills" description="Select skills for the assignment">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <ListItem onClick={!applyToAll ? undefined : () => next(CascadingFocusPanelIds.SelectSkills)}>
            <div style={{ display: 'grid', gap: 15, alignItems: 'center', gridTemplateColumns: `auto ${isMobile ? '1fr' : 'auto 1fr auto'} auto` }}>
              <div onClick={event => event.stopPropagation()}>
                <Toggle id="applyToAll" name="applyToAll" toggleForegroundColour="white" value={applyToAll} onChange={() => setApplyToAll(!applyToAll)} />
              </div>

              {isMobile ? (
                <div>
                  <h5>Apply same skills to all</h5>

                  <p style={{ color: 'var(--text_1)' }}>Choose particular skills and apply it to the group of people</p>

                  <div style={{ marginTop: 4 }}>
                    <div>{!applyToAll ? <Chip>{selectedSkills.length} skill(s)</Chip> : <Chip customTheme={CUSTOM_THEME}>{selectedSkills.length} skill(s)</Chip>}</div>
                  </div>
                </div>
              ) : (
                <React.Fragment>
                  <h5>Apply same skills to all</h5>

                  <p style={{ color: 'var(--text_1)' }}>Choose particular skills and apply it to the group of people</p>

                  <div>{!applyToAll ? <Chip>{selectedSkills.length} skill(s)</Chip> : <Chip customTheme={CUSTOM_THEME}>{selectedSkills.length} skill(s)</Chip>}</div>
                </React.Fragment>
              )}

              <ChevronIcon tone={!applyToAll ? 'default' : 'primary'} />
            </div>
          </ListItem>

          <div>
            <h4>Assigned people & skills</h4>

            <div style={{ marginTop: 12, height: 400, overflowY: 'auto' }}>
              <ListLayout>
                {selectedPeople?.map(person => (
                  <ListItemLayout
                    key={person.learnerSlug}
                    disabled={applyToAll}
                    onClick={() => {
                      setSelectedPerson(person);
                      next(CascadingFocusPanelIds.SelectSkills);
                    }}
                  >
                    <div className={classNames('card', styles.listItem)} style={{ gridTemplateColumns: isMobile ? '1fr auto' : '1fr 1fr 195px auto' }}>
                      {isMobile ? (
                        <div style={{ display: 'flex', flexDirection: 'column', rowGap: 4 }}>
                          <div className="body" style={{ color: 'var(--accent-2)' }}>
                            {person.firstName} {person.lastName}
                          </div>

                          <small>
                            {person.departmentName} | {person.teamName}
                          </small>

                          <small>{person.email}</small>

                          <div style={{ marginTop: 12, display: 'flex', gap: 12 }}>{generateChips(person)}</div>
                        </div>
                      ) : (
                        <React.Fragment>
                          <div>
                            <div className="body" style={{ color: 'var(--accent-2)' }}>
                              {person.firstName} {person.lastName}
                            </div>

                            <small>
                              {person.departmentName} | {person.teamName}
                            </small>
                          </div>

                          <small>{person.email}</small>

                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>{generateChips(person)}</div>
                        </React.Fragment>
                      )}

                      <ChevronIcon tone={applyToAll ? 'default' : 'primary'} />
                    </div>
                  </ListItemLayout>
                ))}
              </ListLayout>
            </div>
          </div>
        </div>

        <div className={classNames('formErrorMessage', { invisible: !errorMessage })}>{errorMessage}&nbsp;</div>
      </EntitiesCardWidget>

      <footer className={classNames('card', layoutStyles.wizardFooter)}>
        <Anchor arrow reverse onClick={onBack}>
          Back
        </Anchor>

        <Anchor
          arrow
          onClick={async () => {
            if (!selectedPeople) return;

            const tempSelectedPeople: IPerson[] = structuredClone(selectedPeople);
            if (applyToAll) tempSelectedPeople.forEach(personToEdit => (personToEdit.skills = selectedSkills));

            if (!!generateErrorMessage(tempSelectedPeople)) return;

            setIsLoading(true);

            // Create the peer endorsement data structure where each person reviews every other person
            const assignees: PeerAssessmentAssigneeRequest[] = tempSelectedPeople.map(person => {
              // Get all reviewers (all other people in the assessment)
              const reviewers = tempSelectedPeople.map(reviewer => ({
                learnerSlug: reviewer.learnerSlug,
                isManager: reviewer.manager,
              }));

              return {
                learnerSlug: person.learnerSlug,
                companySkillIds: person.skills.map(skill => skill.companyEntityId!),
                reviewers: reviewers,
              };
            });

            await actions.updatePeerAssessment({
              assessmentSlug: assessmentSlug,
              peerAssessment: {
                isSkillsFeedbackEnabled: false,
                isAssessmentFeedbackEnabled: false,
                isAnonymous: false,
                isWeightingEnabled: false,
                assignees: assignees,
              },
            });

            setPeople(tempSelectedPeople);
            onNext();
            setIsLoading(false);
          }}
        >
          Next
        </Anchor>
      </footer>

      <CascadingPanels
        openPanelIds={openPanelIds}
        onClosePanel={id => {
          previous();
          switch (id) {
            case CascadingFocusPanelIds.SelectSkills: {
              setSelectedPerson(undefined);
              break;
            }

            case CascadingFocusPanelIds.ImportSkills: {
              setSync(crypto.randomUUID());
              break;
            }
          }
        }}
      >
        <CascadingPanel id={CascadingFocusPanelIds.SelectSkills}>
          <SelectSkillsLayout
            roleSlugs={!!roleSlug ? [roleSlug] : selectedPerson?.roleSlugs}
            personToEdit={selectedPerson}
            skills={selectedPerson?.skills ?? selectedSkills}
            sync={sync}
            onBack={() => {
              previous();
              setSelectedPerson(undefined);
            }}
            onAssign={selectedSkills => {
              !!selectedPerson
                ? setSelectedPeople(currentState => {
                  if (!currentState) return;
                  const nextState: IPerson[] = structuredClone(currentState);
                  const nextPerson = nextState.find(person => person.learnerSlug === selectedPerson.learnerSlug);
                  if (!!nextPerson) nextPerson.skills = selectedSkills;
                  return nextState;
                })
                : setSelectedSkills(selectedSkills);
              previous();
              setSelectedPerson(undefined);
            }}
          />
        </CascadingPanel>

        <CascadingPanel id={CascadingFocusPanelIds.ImportSkills}>
          <ImportSkillPanel
            supertitle="Assessments"
            onClose={() => {
              previous();
              setSync(crypto.randomUUID());
            }}
          />
        </CascadingPanel>
      </CascadingPanels>
    </React.Fragment>
  );
}
